import { useState } from 'react';
import { generateEssayPrompt, gradeEssay } from '../api';

export default function EssayPractice() {
  const [phase, setPhase] = useState('intro'); // intro | loading | writing | grading | results
  const [essayData, setEssayData] = useState(null);
  const [essayText, setEssayText] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [grade, setGrade] = useState(null);
  const [error, setError] = useState(null);

  async function handleStart() {
    setPhase('loading');
    setError(null);
    try {
      const data = await generateEssayPrompt();
      setEssayData(data);
      setEssayText('');
      setWordCount(0);
      setGrade(null);
      setPhase('writing');
    } catch (e) {
      setError(e.message || 'Failed to load prompt. Please try again.');
      setPhase('intro');
    }
  }

  function handleChange(e) {
    const text = e.target.value;
    setEssayText(text);
    setWordCount(text.trim() === '' ? 0 : text.trim().split(/\s+/).length);
  }

  async function handleSubmit() {
    setPhase('grading');
    setError(null);
    try {
      const result = await gradeEssay(essayData.prompt, essayText);
      setGrade(result);
      setPhase('results');
    } catch (e) {
      setError(e.message || 'Failed to grade essay. Please try again.');
      setPhase('writing');
    }
  }

  // ── INTRO ──
  if (phase === 'intro') {
    return (
      <div className="essay-practice-intro">
        <div className="essay-practice-icon">✍️</div>
        <h1 className="essay-practice-title">Essay Practice</h1>
        <p className="essay-practice-desc">
          Get a real GED-style passage and writing prompt. Write your response,
          then receive instant AI feedback scored on the official GED rubric.
        </p>
        <div className="essay-practice-rubric">
          <p className="essay-practice-rubric-label">Scored on 3 traits (0–2 each, 6 points total):</p>
          <ul>
            <li>📝 Analysis of Arguments</li>
            <li>🏗️ Development &amp; Organization</li>
            <li>✏️ Language Use &amp; Conventions</li>
          </ul>
        </div>
        {error && <p className="exam-error">{error}</p>}
        <button
          className="primary-btn"
          style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', marginTop: 8 }}
          onClick={handleStart}
        >
          Generate Essay Prompt
        </button>
      </div>
    );
  }

  // ── LOADING ──
  if (phase === 'loading') {
    return (
      <div className="exam-loading">
        <div className="exam-spinner" style={{ borderTopColor: '#6366f1' }} />
        <p>Generating essay prompt…</p>
      </div>
    );
  }

  // ── GRADING ──
  if (phase === 'grading') {
    return (
      <div className="exam-loading">
        <div className="exam-spinner" style={{ borderTopColor: '#6366f1' }} />
        <p>AI is grading your essay…</p>
        <p className="exam-grading-sub">This takes about 10 seconds</p>
      </div>
    );
  }

  // ── WRITING ──
  if (phase === 'writing' && essayData) {
    return (
      <div className="essay-screen">
        <div className="essay-header">
          <span className="essay-label">Extended Response Practice</span>
          <span className="exam-q-counter">Language Arts</span>
        </div>
        <div className="essay-passage">
          <p className="essay-passage-label">Read the following passage:</p>
          <p className="essay-passage-text">{essayData.passage}</p>
        </div>
        <div className="essay-prompt-box">
          <p className="essay-prompt-text">{essayData.prompt}</p>
        </div>
        <div className="essay-write-area">
          <textarea
            className="essay-textarea"
            placeholder="Write your response here…"
            value={essayText}
            onChange={handleChange}
            rows={12}
          />
          <div className="essay-footer">
            <span className={`essay-wordcount ${wordCount >= 50 ? 'essay-wordcount--ok' : ''}`}>
              {wordCount} words {wordCount < 50 ? '(min 50)' : '✓'}
            </span>
            <button
              className="primary-btn"
              style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}
              disabled={wordCount < 50}
              onClick={handleSubmit}
            >
              Submit for Grading
            </button>
          </div>
        </div>
        {error && <p className="exam-error" style={{ marginTop: 12 }}>{error}</p>}
      </div>
    );
  }

  // ── RESULTS ──
  if (phase === 'results' && grade) {
    const scoreColor = grade.total >= 5 ? '#4caf50' : grade.total >= 3 ? '#ff9800' : '#e53935';
    return (
      <div className="essay-results">
        <div className="essay-results-header">
          <div className="essay-results-score" style={{ color: scoreColor }}>{grade.total}/6</div>
          <p className="essay-results-label">Essay Score</p>
          <p className="essay-grade-summary-text">{grade.summary}</p>
        </div>

        <div className="essay-traits">
          {[grade.trait1, grade.trait2, grade.trait3].map(t => (
            <div key={t.name} className="essay-trait-card">
              <div className="essay-trait-header">
                <span className="essay-trait-name">{t.name}</span>
                <span className={`essay-trait-score essay-trait-score--${t.score}`}>{t.score}/2</span>
              </div>
              <p className="essay-trait-feedback">{t.feedback}</p>
            </div>
          ))}
        </div>

        <div className="essay-results-actions">
          <button
            className="primary-btn"
            style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}
            onClick={handleStart}
          >
            Try Another Prompt
          </button>
          <button className="secondary-btn" onClick={() => setPhase('writing')}>
            Revise My Essay
          </button>
        </div>
      </div>
    );
  }

  return null;
}
